import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getToken } from 'firebase/app-check';

import { appCheck, scoringApp } from '../firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface JoinTryoutResponse {
  customToken: string;
  tryoutId: string;
  tryoutName: string;
}

interface ErrorResponse {
  error: string;
}

export interface JoinResult {
  tryoutId: string;
  tryoutName: string;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class JoinError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JoinError';
  }
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const FUNCTION_URL =
  'https://us-central1-cheereval.cloudfunctions.net/joinTryout';

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * POST to the joinTryout Cloud Function with an App Check token, then sign
 * the scoring coach into Firebase Auth using the custom token returned by
 * the server.
 */
export async function joinTryout(code: string, coachName: string, password: string, forceRefresh = false): Promise<JoinResult> {
  // Obtain an App Check token to prove this request comes from the real app.
  // The server will reject requests that are missing or have invalid tokens.
  let appCheckToken: string;
  try {
    const { token } = await getToken(appCheck, forceRefresh);
    appCheckToken = token;
  } catch (err) {
    console.error('[joinTryout] App Check getToken failed:', err);
    throw new JoinError('App verification failed. Please reload the page and try again.');
  }

  let response: globalThis.Response;
  try {
    response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': appCheckToken,
      },
      body: JSON.stringify({ code, coachName, password }),
    });
  } catch (err) {
    console.error('[joinTryout] fetch failed:', err);
    throw new JoinError('Could not reach the server. Please check your connection and try again.');
  }

  if (!response.ok) {
    let message = 'Failed to join. Please try again.';
    try {
      const body = (await response.json()) as ErrorResponse;
      console.error('[joinTryout] server error', response.status, body);
      if (typeof body.error === 'string' && body.error.length > 0) {
        message = body.error;
      }
    } catch {
      console.error('[joinTryout] server returned HTTP', response.status, '(no JSON body)');
    }
    throw new JoinError(message);
  }

  const data = (await response.json()) as JoinTryoutResponse;

  // Sign into the isolated scoring app — never touches the owner-coach session.
  const auth = getAuth(scoringApp);
  try {
    await signInWithCustomToken(auth, data.customToken);
  } catch (err) {
    console.error('[joinTryout] signInWithCustomToken failed:', err);
    throw new JoinError('Failed to sign in after joining. Please try again.');
  }

  return { tryoutId: data.tryoutId, tryoutName: data.tryoutName };
}
